import React, {
    MutableRefObject,
    PropsWithChildren,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    BreaksRenderType,
    getVirtualBreaks,
    haveBreaksChanged,
} from './timebreaks';
import {
    buildTimePointerContext,
    TimePointerContext,
} from './TimePointerContext';
import {
    TimeSeriesDisableRange,
    TimeSeriesDisableRangeLabel,
} from 'utils/timeseries/TimeSeriesDisableRange';
import { TimeLineLabel } from './timeline';
import {
    discontinuityRange,
    scaleDiscontinuous,
} from '@d3fc/d3fc-discontinuous-scale';
import { scaleTime } from 'd3-scale';
import { ZoomControl } from 'utils/components/Zoom/chartZoom';
import { ZoomContextProvider } from 'utils/components/Zoom/ZoomContext';

export interface XAxisContextState {
    timeSeriesChartWidth: number;
    plotAreaWidth: number;
    setPlotAreaWidth: (v: number) => void;
    isDomainValid: boolean;
    breaks: TimeSeriesDisableRange[];
    breaksType?: BreaksRenderType;
    rangeLabels: TimeSeriesDisableRangeLabel[];
    timeLineLabels: TimeLineLabel[];
    timePointerRef: MutableRefObject<TimePointerContext>;
    startTime: number;
    endTime: number;
    showTimeLineDate: boolean;
}

export const XAxisContext = React.createContext<XAxisContextState | undefined>(
    undefined
);

interface XAxisContextProviderProps {
    timeSeriesChartWidth: number;
    startTime: number;
    endTime: number;
    breaks: TimeSeriesDisableRange[];
    breaksType?: BreaksRenderType;
    rangeLabels: TimeSeriesDisableRangeLabel[];
    timeLineLabels: TimeLineLabel[];
    allowZoom?: boolean;
    resetZoomOnTimeRangeChange?: boolean;
    onZoomScaleBoundary?: () => void;
    onZoomIn?: (c: ZoomControl) => void;
    onZoomOut?: (c: ZoomControl) => void;
    suppressTimeLine?: boolean;
    showTimeLineDate?: boolean;
}

interface BreakRef {
    startTime: number;
    endTime: number;
    breaks: TimeSeriesDisableRange[];
}

const ONE_MINUTE = 60 * 1000;
export function XAxisContextProvider(
    props: PropsWithChildren<XAxisContextProviderProps>
) {
    const {
        allowZoom,
        children,
        timeSeriesChartWidth: width,
        resetZoomOnTimeRangeChange = true,
        startTime = 0,
        endTime = 0,
        breaks,
        breaksType,
        rangeLabels,
        timeLineLabels,
        suppressTimeLine,
        onZoomIn,
        onZoomOut,
        onZoomScaleBoundary,
        showTimeLineDate,
    } = props;

    const scrollBarWidth = 10;
    const timeSeriesChartWidth = width - scrollBarWidth / 2;

    const timePointerRef = useRef<TimePointerContext | undefined>();
    const [previousBreaks, setPreviousBreaks] = useState<BreakRef>({
        startTime,
        endTime,
        breaks,
    });
    const previousRangeLabelsRef = useRef<TimeSeriesDisableRangeLabel[]>([]);
    const previousTimeLineLabelsRef = useRef<TimeLineLabel[]>([]);
    const [plotAreaWidth, setPlotAreaWidth] = useState(0);

    if (!timePointerRef.current) {
        const showTimeLine = !suppressTimeLine;
        timePointerRef.current = buildTimePointerContext(showTimeLine);
    }
    useEffect(() => {
        return () => {
            timePointerRef.current && timePointerRef.current.unsubscribeAll();
        };
    }, []);

    const [actualBreaks, setActualBreaks] =
        useState<TimeSeriesDisableRange[]>(breaks);
    const [virtualBreaks, setVirtualBreaks] = useState<
        TimeSeriesDisableRange[]
    >(
        getVirtualBreaks(
            timeSeriesChartWidth,
            startTime - ONE_MINUTE,
            endTime + ONE_MINUTE,
            breaks
        )
    );

    useEffect(() => {
        if (hasBreaksRefChanged(previousBreaks, startTime, endTime, breaks)) {
            setActualBreaks(breaks);
            setVirtualBreaks(
                getVirtualBreaks(
                    timeSeriesChartWidth,
                    startTime - ONE_MINUTE,
                    endTime + ONE_MINUTE,
                    breaks
                )
            );

            setPreviousBreaks({ startTime, endTime, breaks });
        }
    }, [startTime, endTime, breaks, previousBreaks, timeSeriesChartWidth]);

    const dataXScale = useMemo(() => {
        const result = scaleDiscontinuous(scaleTime())
            .domain([startTime, endTime])
            .discontinuityProvider(
                discontinuityRange(...virtualBreaks.map((b) => [b.from, b.to]))
            );

        if (plotAreaWidth > 0) {
            result.range([0, plotAreaWidth]);
        }

        return result;
    }, [startTime, endTime, plotAreaWidth, virtualBreaks]);

    const boundaries = useMemo(
        () => [startTime, endTime],
        [startTime, endTime]
    );

    const [actualrangeLabels, setActualrangeLabels] = useState<
        TimeSeriesDisableRangeLabel[]
    >([]);
    useEffect(() => {
        if (
            haveRangeLabelsChanged(previousRangeLabelsRef.current, rangeLabels)
        ) {
            setActualrangeLabels(rangeLabels);
            previousRangeLabelsRef.current = rangeLabels;
        }
    }, [rangeLabels, setActualrangeLabels]);

    const [actualTimeLineLabels, setActualTimeLineLabels] = useState<
        TimeLineLabel[]
    >([]);
    useEffect(() => {
        if (
            haveTimeLineLabelsChanged(
                previousTimeLineLabelsRef.current,
                timeLineLabels
            )
        ) {
            setActualTimeLineLabels(timeLineLabels);
            previousTimeLineLabelsRef.current = timeLineLabels;
        }
    }, [timeLineLabels, setActualTimeLineLabels]);

    return (
        <XAxisContext.Provider
            value={{
                timeSeriesChartWidth,
                plotAreaWidth,
                setPlotAreaWidth,
                isDomainValid: !!startTime && !!endTime && endTime >= startTime,
                breaks: actualBreaks,
                breaksType,
                rangeLabels: actualrangeLabels,
                timeLineLabels: actualTimeLineLabels,
                timePointerRef:
                    timePointerRef as MutableRefObject<TimePointerContext>,
                startTime,
                endTime,
                showTimeLineDate: !!showTimeLineDate,
            }}
        >
            <ZoomContextProvider
                allowZoom={allowZoom}
                horizontal={{ boundaries, dataXScale, plotAreaWidth }}
                onZoomIn={onZoomIn}
                onZoomOut={onZoomOut}
                onZoomScaleBoundary={onZoomScaleBoundary}
                resetZoomOnBoundaryChange={resetZoomOnTimeRangeChange}
            >
                {children}
            </ZoomContextProvider>
        </XAxisContext.Provider>
    );
}

export function useXAxisContext() {
    return useContext(XAxisContext)!;
}

function haveRangeLabelsChanged(
    prev: TimeSeriesDisableRangeLabel[],
    next: TimeSeriesDisableRangeLabel[]
) {
    if (prev.length !== next.length) {
        return true;
    }
    const length = prev.length;
    if (length > 0) {
        for (let i = 0; i < length; i++) {
            if (
                prev[i].close !== next[i].close ||
                prev[i].open !== next[i].open ||
                prev[i].closeLabel !== next[i].closeLabel ||
                prev[i].openLabel !== next[i].openLabel
            ) {
                return true;
            }
        }
    }
    return false;
}

function haveTimeLineLabelsChanged(
    prev: TimeLineLabel[],
    next: TimeLineLabel[]
) {
    if (prev.length !== next.length) {
        return true;
    }
    const length = prev.length;
    for (let i = 0; i < length; i++) {
        if (
            prev[i].from !== next[i].from ||
            prev[i].to !== next[i].to ||
            prev[i].label !== next[i].label
        ) {
            return true;
        }
    }
    return false;
}

function hasBreaksRefChanged(
    prefRef: BreakRef,
    startTime: number,
    endTime: number,
    breaks: TimeSeriesDisableRange[]
) {
    return (
        startTime !== prefRef.startTime ||
        endTime !== prefRef.endTime ||
        haveBreaksChanged(prefRef.breaks, breaks)
    );
}
